import { DEFAULT_BINDINGS, actionOfPress } from "../controls/bindings.ts";
import { actionPress } from "../controls/press.ts";

/**
 * The stance (ADR-073's amendment "the stance: a weapon put away", the author's ruling of 2026-10-02, P2.2-T13): whether
 * the page's own character holds its weapon drawn or sheathed. Sheathed, a left click on the world only selects what
 * stands there and never swings; drawn, it selects and swings as before. A press on an ability (a slot's key or a click
 * on a slot) draws it and goes through; an ability's hit landing on the character draws it (ADR-073's note of
 * 2026-10-03, "being attacked draws the weapon"); R toggles it; a welcome and a respawn sheathe it. The stance is the
 * page's intent alone: the map server never hears it, watchers see nothing of it, and being sheathed protects no one.
 */

/**
 * The two stances.
 */
export type Stance = "drawn" | "sheathed";

/**
 * What moves the stance: the key or the HUD's button (`toggle`), a press on a slot holding an ability (`ability`), an
 * ability's hit landing on the character, blocked or immune included and a periodic effect's tick excluded
 * (`attacked`), a session's welcome (`welcome`) and a stand after death (`respawn`).
 */
export type StanceEvent = "toggle" | "ability" | "attacked" | "welcome" | "respawn";

/**
 * The physical key that toggles the stance before a player chooses another (`controls/`).
 */
export const STANCE_KEY = DEFAULT_BINDINGS.stance;

/**
 * The stance after an event.
 *
 * @param stance The stance now.
 * @param event What happened.
 * @returns The stance after it.
 * @example
 * stanceAfter("sheathed", "ability");
 * // "drawn"
 */
export function stanceAfter(stance: Stance, event: StanceEvent): Stance {
  if (event === "toggle") {
    return stance === "drawn" ? "sheathed" : "drawn";
  }
  return event === "ability" || event === "attacked" ? "drawn" : "sheathed";
}

/**
 * A press as the stance reads it: its key, its physical key when the event says, whether it repeats, the modifiers the
 * native event carries and what had focus, which decides a stance bound to Tab (`controls/press.ts`).
 */
export interface StancePress {
  readonly key: string;
  readonly code?: string | undefined;
  readonly repeat: boolean;
  readonly shiftKey?: boolean;
  readonly ctrlKey?: boolean;
  readonly altKey?: boolean;
  readonly metaKey?: boolean;
  readonly target?: unknown;
}

/**
 * Whether a press is of R, the stance's key before a player chooses another.
 *
 * @param event The press.
 * @returns True for R in either case, Tab's focus rule aside.
 */
function defaultStancePress(event: StancePress): boolean {
  return actionPress({ actionOf: (press) => actionOfPress(DEFAULT_BINDINGS, press) }, "stance", event);
}

/**
 * What a key press does to the stance: `toggle` for the stance's key with no Ctrl, Alt or Meta, `repeat` for its
 * auto-repeat, which is taken and does nothing so a held key never toggles again, and `leave` for anything else, which
 * is left to the browser (Ctrl+R still reloads) or to another binding.
 *
 * @param event The press.
 * @param isStance Whether the press is the stance's key; the page asks its key table (`controls/press.ts`
 *   `actionPress`), and R in either case answers unless given.
 * @returns What to do with it.
 * @example
 * stanceKeyAction({ key: "r", repeat: false, ctrlKey: true });
 * // "leave"
 */
export function stanceKeyAction(event: StancePress, isStance: (event: StancePress) => boolean = defaultStancePress): "toggle" | "repeat" | "leave" {
  if (!isStance(event) || event.ctrlKey === true || event.altKey === true || event.metaKey === true) {
    return "leave";
  }
  return event.repeat ? "repeat" : "toggle";
}

/**
 * Whether a left click on the world swings in a stance: only drawn.
 *
 * @param stance The stance.
 * @returns True when the click swings as well as selects.
 */
export function clickSwings(stance: Stance): boolean {
  return stance === "drawn";
}

/**
 * The stance as the fight moves it and the HUD's button draws it: the page holds one, which the fight applies events to
 * and React reads through `subscribe` and `getSnapshot`; the button's press goes through `toggle` to the control the
 * session binds, so a toggle from the pointer and one from the key take the same path.
 *
 * @example
 * ```ts
 * const stance = new StanceStore();
 * stance.apply("ability");
 * stance.getSnapshot();
 * // "drawn"
 * ```
 */
export class StanceStore {
  private stance: Stance = "sheathed";
  private readonly listeners = new Set<() => void>();
  private toggleControl: () => boolean = () => false;

  /**
   * Adds a listener told of every change. Works unbound, as React calls it.
   *
   * @param listener Called after each change.
   * @returns A function that removes it.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * The stance now. Works unbound, as React calls it.
   *
   * @returns The stance.
   */
  public readonly getSnapshot = (): Stance => this.stance;

  /**
   * Applies an event, telling the listeners when the stance changed.
   *
   * @param event What happened.
   * @returns The stance after it.
   */
  public apply(event: StanceEvent): Stance {
    const next = stanceAfter(this.stance, event);
    if (next !== this.stance) {
      this.stance = next;
      for (const listener of this.listeners) {
        listener();
      }
    }
    return next;
  }

  /**
   * Binds what a toggle asks: the session's fight, while there is one.
   *
   * @param control Toggles the stance, answering whether it did.
   */
  public bindToggle(control: () => boolean): void {
    this.toggleControl = control;
  }

  /**
   * Toggles the stance through the bound control, as the HUD's button and the key do.
   *
   * @returns True when it toggled.
   */
  public toggle(): boolean {
    return this.toggleControl();
  }
}
